import { useQuery } from "@tanstack/react-query";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";

import { triggersV2QueryKeys } from "./triggersV2QueryKeys";

/** One trigger by id, in the unified v2 shape. */
export function useTriggerV2ById(
  triggerId: string | undefined,
  orgId?: string,
) {
  const { triggerV2Service } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(orgId);

  return useQuery({
    queryKey: triggersV2QueryKeys.byId(resolvedOrgId, triggerId ?? ""),
    queryFn: ({ signal }) => {
      if (triggerId === undefined) {
        return Promise.reject(new Error("Invalid trigger id"));
      }
      return triggerV2Service.getTriggerById(triggerId, {
        abortSignal: signal,
      });
    },
    enabled: triggerId !== undefined,
    staleTime: 30_000,
  });
}

/** One trigger by name, in the unified v2 shape. */
export function useTriggerV2(name: string | undefined, orgId?: string) {
  const { triggerV2Service } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(orgId);

  return useQuery({
    queryKey: triggersV2QueryKeys.byName(resolvedOrgId, name ?? ""),
    queryFn: ({ signal }) => {
      if (name === undefined) {
        return Promise.reject(new Error("Invalid trigger name"));
      }
      return triggerV2Service.getTriggerByName(name, resolvedOrgId, {
        abortSignal: signal,
      });
    },
    enabled: name !== undefined,
    staleTime: 30_000,
  });
}
